import React, { useState, useEffect } from 'react';
import { X, Wifi, Loader2, Check, AlertCircle } from 'lucide-react';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import type { Project } from '../../types';

interface ProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (project: Partial<Project> & { wordpress_connected?: boolean | null }) => void;
  project?: Project | null;
  mode: 'create' | 'edit';
}

type ConnectionStatus = 'idle' | 'testing' | 'success' | 'error';

export const ProjectModal: React.FC<ProjectModalProps> = ({
  isOpen,
  onClose,
  onSave,
  project,
  mode
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    targetKeyword: '',
    status: 'completed' as Project['status'],
    url: '',
    username: '',
    password: ''
  });

  const [noWordPress, setNoWordPress] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('idle');
  const [connectionMessage, setConnectionMessage] = useState('');

  const MASKED_PASSWORD = '••••••••';

  useEffect(() => {
    if (project && mode === 'edit') {
      const wpConnected = (project as any).wordpress_connected;
      setFormData({
        name: project.name || '',
        description: project.description || '',
        targetKeyword: project.targetKeyword || '',
        status: project.status || 'draft',
        url: (project as any).wordpress_url || '',
        username: (project as any).wordpress_username || '',
        password: MASKED_PASSWORD
      });
      setNoWordPress(wpConnected === false);
      if (wpConnected === true) {
        setConnectionStatus('success');
        setConnectionMessage('Conexión verificada');
      }
    } else {
      setFormData({
        name: '',
        description: '',
        targetKeyword: '',
        status: 'completed',
        url: '',
        username: '',
        password: ''
      });
      setNoWordPress(false);
      setConnectionStatus('idle');
      setConnectionMessage('');
    }
  }, [project, mode, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const testConnection = async () => {
    if (!formData.url || !formData.username) {
      setConnectionStatus('error');
      setConnectionMessage('Se requiere URL y usuario');
      return;
    }

    if (!project?.id && formData.password === MASKED_PASSWORD) {
      setConnectionStatus('error');
      setConnectionMessage('Introduce la contraseña');
      return;
    }

    setConnectionStatus('testing');
    setConnectionMessage('Probando...');

    try {
      const response = await fetch('/api/wordpress/test-connection', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: formData.url,
          username: formData.username,
          password: formData.password,
          projectId: project?.id
        })
      });

      const result = await response.json();

      if (result.success) {
        setConnectionStatus('success');
        setConnectionMessage(result.message || 'Conexión OK');
      } else {
        setConnectionStatus('error');
        setConnectionMessage(result.message || 'Error de conexión');
      }
    } catch (error) {
      setConnectionStatus('error');
      setConnectionMessage('Error al probar');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (mode === 'create' && !noWordPress) {
      if (formData.url && formData.username && formData.password) {
        if (connectionStatus !== 'success') {
          setConnectionStatus('error');
          setConnectionMessage('Prueba la conexión primero');
          return;
        }
      }
    }

    const payload: any = { ...formData };

    if (noWordPress) {
      payload.wordpress_connected = false;
      payload.url = '';
      payload.username = '';
      payload.password = '';
    } else if (connectionStatus === 'success') {
      payload.wordpress_connected = true;
    } else {
      payload.wordpress_connected = null;
    }

    if (mode === 'edit') {
      if (!payload.password || payload.password === MASKED_PASSWORD) {
        delete payload.password;
      }
    }

    onSave(payload);
    onClose();
  };

  const handleNoWordPressChange = (checked: boolean) => {
    setNoWordPress(checked);
    if (checked) {
      setConnectionStatus('idle');
      setConnectionMessage('');
    }
  };

  if (!isOpen) return null;

  const hasCredentials = formData.url && formData.username && formData.password && formData.password !== MASKED_PASSWORD;
  const canSubmit = mode === 'edit' || noWordPress || connectionStatus === 'success';

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-start sm:items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl max-h-[calc(100vh-2rem)] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">
            {mode === 'create' ? 'Nuevo Proyecto' : 'Editar Proyecto'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-6 py-5 space-y-5">

            {/* Nombre del Proyecto */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Nombre <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Nombre del proyecto"
                required
                className="w-full bg-gray-50 border border-gray-200 focus:bg-white focus:border-gray-400 focus:ring-0 rounded-lg px-3 py-2.5 text-sm"
              />
            </div>

            {/* Descripción */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Descripción
              </label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descripción breve (opcional)"
                rows={3}
                className="w-full bg-gray-50 border border-gray-200 focus:bg-white focus:border-gray-400 focus:ring-0 rounded-lg px-3 py-2.5 text-sm resize-none"
              />
            </div>

            {/* Separador */}
            <div className="border-t border-gray-100 pt-5">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-gray-700">WordPress</span>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={noWordPress}
                    onChange={(e) => handleNoWordPressChange(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-500"
                  />
                  <span className="text-xs text-gray-500">Sin WordPress</span>
                </label>
              </div>

              {/* Campos WordPress */}
              <div className={`space-y-4 ${noWordPress ? 'opacity-40 pointer-events-none' : ''}`}>
                {/* URL */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    URL del sitio
                  </label>
                  <Input
                    type="url"
                    value={formData.url}
                    onChange={(e) => {
                      setFormData({ ...formData, url: e.target.value });
                      setConnectionStatus('idle');
                    }}
                    placeholder="https://tusitio.com"
                    disabled={noWordPress}
                    className="w-full bg-gray-50 border border-gray-200 focus:bg-white focus:border-gray-400 focus:ring-0 rounded-lg px-3 py-2.5 text-sm"
                  />
                </div>

                {/* Usuario y Contraseña */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">
                      Usuario
                    </label>
                    <Input
                      type="text"
                      value={formData.username}
                      onChange={(e) => {
                        setFormData({ ...formData, username: e.target.value });
                        setConnectionStatus('idle');
                      }}
                      placeholder="usuario"
                      disabled={noWordPress}
                      className="w-full bg-gray-50 border border-gray-200 focus:bg-white focus:border-gray-400 focus:ring-0 rounded-lg px-3 py-2.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">
                      Contraseña app
                    </label>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => {
                        setFormData({ ...formData, password: e.target.value });
                        setConnectionStatus('idle');
                      }}
                      placeholder="xxxx xxxx xxxx"
                      disabled={noWordPress}
                      className="w-full bg-gray-50 border border-gray-200 focus:bg-white focus:border-gray-400 focus:ring-0 rounded-lg px-3 py-2.5 text-sm"
                    />
                  </div>
                </div>

                {/* Botón Probar Conexión */}
                {!noWordPress && (
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={testConnection}
                      disabled={connectionStatus === 'testing' || !hasCredentials}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        connectionStatus === 'testing'
                          ? 'bg-gray-100 text-gray-500'
                          : connectionStatus === 'success'
                          ? 'bg-emerald-50 text-emerald-700'
                          : connectionStatus === 'error'
                          ? 'bg-red-50 text-red-700'
                          : 'bg-gray-900 text-white hover:bg-gray-800'
                      }`}
                    >
                      {connectionStatus === 'testing' ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Probando</>
                      ) : connectionStatus === 'success' ? (
                        <><Check className="w-4 h-4" /> Conectado</>
                      ) : connectionStatus === 'error' ? (
                        <><AlertCircle className="w-4 h-4" /> Reintentar</>
                      ) : (
                        <><Wifi className="w-4 h-4" /> Probar</>
                      )}
                    </button>
                    {connectionMessage && (
                      <span className={`text-xs ${
                        connectionStatus === 'success' ? 'text-emerald-600' :
                        connectionStatus === 'error' ? 'text-red-600' : 'text-gray-500'
                      }`}>
                        {connectionMessage}
                      </span>
                    )}
                  </div>
                )}

                {/* Aviso */}
                {mode === 'create' && connectionStatus !== 'success' && !noWordPress && hasCredentials && (
                  <p className="text-xs text-amber-600 flex items-center gap-1.5">
                    <AlertCircle className="w-3.5 h-3.5" />
                    Prueba la conexión antes de crear
                  </p>
                )}
              </div>
            </div>

            {/* Estado (solo en modo editar) */}
            {mode === 'edit' && (
              <div className="border-t border-gray-100 pt-5">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Estado
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as Project['status'] })}
                  className="w-full bg-gray-50 border border-gray-200 focus:bg-white focus:border-gray-400 focus:ring-0 rounded-lg px-3 py-2.5 text-sm"
                >
                  <option value="draft">Borrador</option>
                  <option value="in_progress">En Progreso</option>
                  <option value="completed">Completado</option>
                  <option value="archived">Archivado</option>
                </select>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={mode === 'create' && !canSubmit}
              className={`px-5 py-2 text-sm font-medium rounded-lg transition-all ${
                mode === 'create' && !canSubmit
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-900 text-white hover:bg-gray-800'
              }`}
            >
              {mode === 'create' ? 'Crear' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
